import { Cluster, ClusterOverlay, LatLongTuple } from '~/components/LERE/types';

import { getClusterColor } from './clusterMapUtils';

const MAX_LON_SPAN_DEGREES = 180;

function ringHasWorldSpan(ring: Array<LatLongTuple>): boolean {
  const longitudes = ring.map((point) => point[1]);
  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  return maxLon - minLon > MAX_LON_SPAN_DEGREES;
}

export function sanitizeRings(rings: Array<Array<LatLongTuple>>): Array<Array<LatLongTuple>> {
  // Prevents polygons that span the entire world from being displayed
  return rings.filter((ring) => ring.length >= 3 && !ringHasWorldSpan(ring));
}

export function parseGeoJsonPolygonRaw(geoJsonStr: string): Array<Array<LatLongTuple>> {
  try {
    const geoJson = JSON.parse(geoJsonStr);
    if (geoJson.type === 'Polygon') {
      return geoJson.coordinates.map((ring: Array<Array<number>>) =>
        ring.map(([lng, lat]: Array<number>) => [lat, lng] as LatLongTuple),
      );
    }
    if (geoJson.type === 'MultiPolygon') {
      return geoJson.coordinates.flatMap((polygon: Array<Array<Array<number>>>) =>
        polygon.map((ring: Array<Array<number>>) =>
          ring.map(([lng, lat]: Array<number>) => [lat, lng] as LatLongTuple),
        ),
      );
    }
  } catch {
    return [];
  }
  return [];
}

export function parseGeoJsonPolygon(geoJsonStr: string): Array<Array<LatLongTuple>> {
  return sanitizeRings(parseGeoJsonPolygonRaw(geoJsonStr));
}

export function buildClusterOverlays(
  clusters: Array<Cluster>,
  manualClusterLabels: Map<number, string>,
  showBorderBreakingClusters = false,
): Array<ClusterOverlay> {
  return clusters
    .map((cluster) => {
      const rawPaths = parseGeoJsonPolygonRaw(cluster.polygon_geojson);
      const paths = showBorderBreakingClusters ? rawPaths : sanitizeRings(rawPaths);
      const manualLabel = manualClusterLabels.get(cluster.cluster_id);
      const tooltipLabel = manualLabel
        ? `${manualLabel} • Cluster ${cluster.cluster_id} • ${cluster.offer_count} offers (manual)`
        : `Cluster ${cluster.cluster_id} • ${cluster.offer_count} offers`;

      return {
        cluster,
        color: getClusterColor(cluster.cluster_id),
        tooltipLabel,
        paths,
      };
    })
    .filter((overlay) => overlay.paths.length > 0);
}
